import { Link, useLocation } from 'react-router-dom';

import { INBODY_COLORS } from '@inbody/core';
import { LayoutHeader, LogoImage } from '@inbody/ui';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { Badge, Box, Button, Chip, IconButton } from '@mui/material';

import { useCart } from '@/pages/v2/api';
import { useMetadataPermission } from '@/pages/v2/hooks';

import { Urls } from '@/shared/lib/react-router/Urls';

/**
 * v2 헤더 — 글로벌포털(insa-portal)과 동일한 @inbody/ui LayoutHeader를 사용한다.
 *
 * insa-portal은 nav를 사이드메뉴에 두지만, indrive는 폴더 탐색 화면에 이미 좌측 폴더트리가
 * 있어 사이드메뉴를 두지 않는다. 대신 LayoutHeader의 leftActions 슬롯에 로고 + nav를,
 * rightActions에 장바구니를 얹고, 테마·언어·계정 메뉴는 LayoutHeader 내장 기능을 쓴다.
 * (테마/언어/계정은 LayoutHeader가 그리므로 로컬 컴포넌트를 따로 렌더하지 않는다.)
 */

type V2NavItem = {
  label: string;
  /** 클릭 시 이동 경로 */
  to: string;
  /** 활성 표시 판별용 prefix 목록 ('/'는 모든 주소의 prefix라 정확히 일치할 때만) */
  match: string[];
};

const V2_NAV: V2NavItem[] = [
  {
    label: '통합검색',
    to: Urls.main.build(),
    match: [Urls.main.path, Urls.search.path],
  },
  // 폴더 미지정 진입 → ArchiveV2EntryPage가 첫 top 폴더로 리다이렉트
  {
    label: '폴더 탐색',
    to: Urls.archive.build(),
    match: [Urls.archive.build()],
  },
  { label: '휴지통', to: Urls.dump.build(), match: [Urls.dump.path] },
];

const isNavActive = (pathname: string, match: string[]) =>
  match.some((m) => (m === '/' ? pathname === '/' : pathname.startsWith(m)));

/** nav 칩 스타일 — 활성 시 브랜드 배경 */
const navChipSx = (isActive: boolean) => ({
  px: { mobile: 0.5, tablet: 1 },
  cursor: 'pointer',
  borderRadius: 2,
  fontWeight: 500,
  fontSize: 14,
  transition: 'all 0.2s ease',
  backgroundColor: isActive ? INBODY_COLORS.brand[600] : 'transparent',
  color: isActive ? INBODY_COLORS.base.white : INBODY_COLORS.gray[700],
  border: 'none',
  '&:hover': {
    backgroundColor: isActive
      ? INBODY_COLORS.brand[700]
      : INBODY_COLORS.gray[50],
  },
});

export const GenericHeaderV2 = () => {
  const location = useLocation();
  // 배지 카운트 — 장바구니 화면과 같은 쿼리 캐시(N14)를 공유한다
  const { data: cartItems = [] } = useCart();
  // 설정 아이콘 노출 게이트 — ADMIN · METADATA_MANAGER · METADATA_EDITOR
  // (실제 차단은 서버의 같은 role 검증 · 규칙 #5)
  const { canAccess: canOpenSettings } = useMetadataPermission();

  const leftActions = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Button
        component={Link}
        to={Urls.main.build()}
        aria-label="logo"
        disableRipple
        sx={{
          flexShrink: 0,
          minWidth: 0,
          px: { mobile: 0.5, tablet: 1 },
          '&:hover': { backgroundColor: 'transparent' },
        }}
      >
        <LogoImage />
      </Button>

      {/* nav — insa-portal은 사이드로 가지만 indrive는 헤더에 유지 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: 0,
          overflowX: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        {V2_NAV.map((item) => {
          const isActive = isNavActive(location.pathname, item.match);
          return (
            <Chip
              key={item.to}
              component={Link}
              to={item.to}
              label={item.label}
              clickable
              variant="filled"
              sx={navChipSx(isActive)}
            />
          );
        })}
      </Box>
    </Box>
  );

  const rightActions = (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {/* 설정(메타데이터 선택지 관리) — 메타데이터 설정 권한자에게만 노출 */}
      {canOpenSettings && (
        <IconButton
          component={Link}
          to={Urls.settingsMetadata.build()}
          aria-label="설정"
          sx={{ color: INBODY_COLORS.gray[700] }}
        >
          <SettingsOutlinedIcon />
        </IconButton>
      )}
      <IconButton
        component={Link}
        to={Urls.cart.build()}
        aria-label="장바구니"
        sx={{
          color: INBODY_COLORS.gray[700],
          '& .MuiBadge-badge': {
            backgroundColor: INBODY_COLORS.brand[600],
            color: INBODY_COLORS.base.white,
          },
        }}
      >
        <Badge badgeContent={cartItems.length}>
          <ShoppingCartOutlinedIcon />
        </Badge>
      </IconButton>
    </Box>
  );

  return (
    <LayoutHeader
      showCorporateChange={false}
      showUserSearch={false}
      accountMenuShowProfile
      leftActions={leftActions}
      rightActions={rightActions}
    />
  );
};
